import React, { useEffect } from 'react';
import styled from 'styled-components';
import FjAuthComponent from 'components/auth-component';
import * as FrontendUtils from 'utils';
import { AuthClientActions, AuthClientSelectors } from 'ducks/auth-client';
import { BD_ACCESS_PORTAL_DOCS } from 'constants/products';
import { CloudActions } from '@portal/portal-core';

import { useAppDispatch, useAppSelector } from 'store/hooks';

import FullPageFormContainer from 'components/full-page-form-container';

import FlowJoPortalHeader from 'images/BDLogotypeTransparentBKGfinal.png';

// add a global text messsage about rebranding of application into the FullPageFormContainer, then inject styling
const FormerlyFlowJo = styled.div`
  text-align: center;
  margin-top: 3px;
  margin-bottom: 8px;
  font-size: 16px;
`;

const PortalAuthHeader = (): React.JSX.Element => (
  <React.Fragment>
    <AuthHeaderImg src={FlowJoPortalHeader} alt={FrontendUtils.APPLICATION_NAME as string} />
    <FormerlyFlowJo>
      formerly FlowJo Portal <a href={BD_ACCESS_PORTAL_DOCS}>(Learn More)</a>
    </FormerlyFlowJo>
    <AuthHeaderSubText>All Solutions. One Account.</AuthHeaderSubText>
  </React.Fragment>
);

const AuthHeaderImg = styled.img`
  margin-top: 10px;
  max-width: 290px;
`;

const AuthHeaderSubText = styled.div`
  margin: 0 auto;
  text-align: center;
  height: 42px;
  font-weight: bold;
  font-size: 18px;
  color: #58585a;
  padding-top: 26px;
`;

// NOTE: the email notification view is currently dependent on this path in router.js.
// when/if that ever changes so should this function.
const navigateToEmailVerificationView = (
  userId: string | null,
  email: string | null,
): void => {
  const emailVerificationQueryParam = 'EMAIL_VERIFICATION_UNVERIFIED';
  const emailVerificationPath = `?${emailVerificationQueryParam}&userId=${userId}&emailAddress=${email}`;
  FrontendUtils.navigate(emailVerificationPath);
};

interface SignInNotification {
  type?: string;
  message?: string;
}

interface SignInCredentials {
  email: string;
  password: string;
}

const SignIn = (): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const auth = useAppSelector((state) => state.auth);
  const emailVerification = useAppSelector((state) =>
    AuthClientSelectors.getEmailVerificationUiState(state.emailVerification),
  );

  useEffect(() => {
    if (auth.emailNotVerified) {
      navigateToEmailVerificationView(auth.userId, auth.email);
    }
  }, [auth.emailNotVerified, auth.userId, auth.email]);

  const onSignIn = ({ email, password }: SignInCredentials): void => {
    dispatch(AuthClientActions.signIn(email, password));
  };

  const displayNotification = (): SignInNotification => {
    if (auth.showNotification === true) {
      return {
        type: auth.notificationType ?? undefined,
        message: auth.notificationMessage ?? undefined,
      };
    }
    if (emailVerification === CloudActions.EMAIL_VERIFICATION_VERIFIED) {
      return {
        type: 'MESSAGE',
        message: 'You can now sign in with your account.',
      };
    }
    return {};
  };

  return (
    <FullPageFormContainer>
      <FjAuthComponent
        renderHeader={() => <PortalAuthHeader />}
        onSignIn={onSignIn}
        forgotPasswordHref="/forgot-password"
        createAccountHref="/create-user"
        learnMoreHref="https://www.flowjo.com/"
        notification={displayNotification()}
      />
    </FullPageFormContainer>
  );
};

export default SignIn;
